import { useEffect, useRef, useState } from 'react';

import {
  drawSpriteThumb,
  loadSpriteRects,
  loadVillageAtlas,
  type SpriteRectsManifest,
} from '@/lib/village-map-preview';

const DEFAULT_SIZE = 32;

type AtlasSpriteThumbProps = {
  /** Atlas sprite key (e.g. `npcPog`, `chicken0`). */
  sprite: string | undefined;
  /** Fallback letter when atlas/sprite is missing. */
  label?: string;
  size?: number;
  className?: string;
  /** Drop the grass chip so the sprite can sit in a line of text. */
  plain?: boolean;
};

let sharedAtlas: HTMLImageElement | null = null;
let sharedRects: SpriteRectsManifest | null = null;
let sharedLoad: Promise<void> | null = null;

function ensureAtlasAssets(): Promise<void> {
  if (sharedAtlas && sharedRects) return Promise.resolve();
  if (!sharedLoad) {
    sharedLoad = Promise.all([loadVillageAtlas(), loadSpriteRects()])
      .then(([atlas, rects]) => {
        sharedAtlas = atlas;
        sharedRects = rects;
      })
      .catch(() => {
        sharedLoad = null;
      });
  }
  return sharedLoad;
}

/**
 * Small atlas-backed thumbnail for NPC / animal inspector rows.
 */
export function AtlasSpriteThumb({
  sprite,
  label,
  size = DEFAULT_SIZE,
  className,
  plain = false,
}: AtlasSpriteThumbProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [ready, setReady] = useState(
    () => sharedAtlas != null && sharedRects != null,
  );

  useEffect(() => {
    let cancelled = false;
    ensureAtlasAssets().then(() => {
      if (!cancelled) setReady(true);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!ready || !sprite || !sharedAtlas || !sharedRects) return;
    const rect = sharedRects.sprites[sprite];
    if (!rect) return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const dpr = window.devicePixelRatio || 1;
    canvas.width = Math.floor(size * dpr);
    canvas.height = Math.floor(size * dpr);
    canvas.style.width = `${size}px`;
    canvas.style.height = `${size}px`;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    drawSpriteThumb(ctx, sharedAtlas, rect, size);
  }, [ready, sprite, size]);

  const letter = (label || sprite || '?').charAt(0).toUpperCase();
  const hasSprite =
    ready && !!sprite && !!sharedRects?.sprites[sprite] && !!sharedAtlas;

  return (
    <span
      className={`inline-flex shrink-0 items-end justify-center overflow-hidden ${plain ? '' : 'rounded-md bg-emerald-50/80'} ${className ?? ''}`}
      style={{ width: size, height: size }}
      aria-hidden
    >
      {hasSprite ? (
        <canvas ref={canvasRef} className="block" />
      ) : (
        <span className="flex h-full w-full items-center justify-center text-xs font-semibold text-gray-500">
          {letter}
        </span>
      )}
    </span>
  );
}
